import { useState, useRef, useEffect } from 'react';
import { Heart, Upload, Image as ImageIcon, Film, Sparkles, Download, CheckCircle, XCircle, Loader2, ArrowLeft, Lock, Plus, Trash2, Settings, Smartphone, Monitor, Square, Facebook, Check } from 'lucide-react';
import axios from 'axios';

const VideoMontageCreatorPro = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [slogan, setSlogan] = useState('');
  const [videoFormat, setVideoFormat] = useState('landscape');
  const [audioFile, setAudioFile] = useState(null);
  const [images, setImages] = useState([]);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [status, setStatus] = useState('idle');
  
  const [generationId, setGenerationId] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImagesOptions, setGeneratedImagesOptions] = useState([]);  // ✅ NOUVEAU
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);  // ✅ NOUVEAU
  const [finalVideo, setFinalVideo] = useState(null);
  
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalVideos, setAdditionalVideos] = useState([]);
  const [namesText, setNamesText] = useState('');
  
  const [availableFormats, setAvailableFormats] = useState({});
  
  const pollInterval = useRef(null);
  const API_URL = 'https://fbapi.tdidigital.io';

  const valentinePrompts = [
    "Romantic rose petals with soft lighting and golden bokeh",
    "Elegant sparkles with Valentine theme and dreamy atmosphere",
    "Luxurious red roses with candlelight and love ambiance",
    "Pink hearts floating in magical romantic scene"
  ];

  const valentineSlogans = [
    "Forever Yours ❤️",
    "Je t'aime pour toujours 💝",
    "Together Forever 💕",
    "Mon Amour Éternel 🌹"
  ];

  useEffect(() => {
    checkAuth();
    fetchFormats();
  }, []);

  const fetchFormats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/video/formats`);
      if (response.data.success) {
        setAvailableFormats(response.data.formats);
      }
    } catch (error) {
      console.error('Error fetching formats:', error);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('audio_token');
    if (token) {
      try {
        const response = await axios.post(`${API_URL}/api/audio/auth/verify`, { token });
        if (response.data.success) {
          setIsAuthenticated(true);
          setCurrentUser(response.data.user);
        }
      } catch (error) {
        localStorage.removeItem('audio_token');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/audio/auth/login`, loginForm);
      if (response.data.success) {
        localStorage.setItem('audio_token', response.data.token);
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
      }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Erreur de connexion');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('audio_token');
    setIsAuthenticated(false);
    setCurrentUser(null);
    resetAll();
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        setError('Fichier audio trop volumineux (max 15MB)');
        return;
      }
      setAudioFile(file);
      setError(null);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setError('Maximum 5 images');
      return;
    }
    setImages(files);
    setError(null);
  };

  const handleUpload = async () => {
    if (!audioFile || images.length === 0 || !prompt.trim()) {
      setError('Veuillez remplir tous les champs requis');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(10);
    setCurrentStep('Upload des fichiers...');

    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      images.forEach(img => formData.append('images', img));
      formData.append('title', title || 'Vidéo St-Valentin');
      formData.append('prompt', prompt);
      formData.append('slogan', slogan);
      formData.append('format', videoFormat);

      const token = localStorage.getItem('audio_token');
      const response = await axios.post(
        `${API_URL}/api/video/upload`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        const genId = response.data.generation_id;
        setGenerationId(genId);
        setAudioDuration(response.data.audio_duration);
        setProgress(20);
        setStep(2);
        
        await generateImage(genId);
        startPollingGeneration(genId);
      }

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Erreur upload');
    }
  };

  const generateImage = async (genId) => {
    setCurrentStep('Génération image AI PRO...');
    setProgress(25);

    try {
      const token = localStorage.getItem('audio_token');
      await axios.post(
        `${API_URL}/api/video/generate-image`,
        { generation_id: genId, image_index: 0 },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Erreur génération image');
    }
  };

  const startPollingGeneration = (genId) => {
    let attempts = 0;
    const maxAttempts = 300;

    pollInterval.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollInterval.current);
        setLoading(false);
        setError('Timeout');
        return;
      }

      try {
        const token = localStorage.getItem('audio_token');
        const response = await axios.get(
          `${API_URL}/api/video/generation/${genId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          const gen = response.data.generation;
          setStatus(gen.status);
          setCurrentStep(gen.current_step);
          setProgress(gen.progress_percent || 0);

          if (gen.status === 'image_ready' && step !== 3) {
            clearInterval(pollInterval.current);
            setGeneratedImage(gen.generated_image_url);
            setGeneratedImagesOptions(gen.generated_images_options || []);  // ✅ STOCKER OPTIONS
            setSelectedImageIndex(gen.selected_image_index || 0);  // ✅ INDEX SÉLECTIONNÉ
            setStep(3);
            setLoading(false);
          }

          if (gen.status === 'assets_ready' && step !== 4) {
            clearInterval(pollInterval.current);
            setStep(4);
            setLoading(false);
          }

          if (gen.status === 'completed') {
            clearInterval(pollInterval.current);
            setFinalVideo(gen.final_video_url);
            setStep(6);
            setLoading(false);
          }

          if (gen.status === 'failed') {
            clearInterval(pollInterval.current);
            setLoading(false);
            setError(gen.error_message);
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 2000);
  };

  // ✅ NOUVELLE FONCTION : Sélectionner image Grok
  const handleSelectGrokImage = async (index) => {
    setSelectedImageIndex(index);
    setGeneratedImage(generatedImagesOptions[index]);
    
    try {
      const token = localStorage.getItem('audio_token');
      await axios.post(
        `${API_URL}/api/video/select-image`,
        { generation_id: generationId, selected_index: index },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      console.log(`✅ Image ${index} sélectionnée`);
    } catch (err) {
      console.error('Erreur sélection image:', err);
    }
  };

  const uploadAdditionalMedia = async () => {
    if (additionalImages.length === 0 && additionalVideos.length === 0) {
      setStep(4);
      return;
    }

    setLoading(true);
    setProgress(50);
    setCurrentStep('Upload médias...');

    try {
      const formData = new FormData();
      formData.append('generation_id', generationId);
      additionalImages.forEach(img => formData.append('images', img));
      additionalVideos.forEach(vid => formData.append('videos', vid));

      const token = localStorage.getItem('audio_token');
      await axios.post(`${API_URL}/api/video/add-media`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      setProgress(55);
      setCurrentStep('Génération ...');
      
      const grokResp = await axios.post(`${API_URL}/api/video/generate-assets-grok`, 
        { generation_id: generationId, names_text: namesText },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (grokResp.data.success && grokResp.data.tasks_created > 0) {
        startPollingAssets(generationId);
      } else {
        setStep(4);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Erreur upload médias');
    }
  };

  const startPollingAssets = (genId) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 120) {
        clearInterval(interval);
        setLoading(false);
        setError('Timeout génération assets');
        return;
      }

      try {
        const token = localStorage.getItem('audio_token');
        const response = await axios.get(`${API_URL}/api/video/status-assets/${genId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success) {
          const { completed, total, all_done, progress_percent } = response.data;
          setCurrentStep(`Assets: ${completed}/${total}`);
          setProgress(55 + (progress_percent * 0.25));
          
          if (all_done) {
            clearInterval(interval);
            setStep(4);
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Poll assets error:', err);
      }
    }, 5000);
  };

  const handleCreateMontage = async () => {
    setLoading(true);
    setStep(5);
    setProgress(85);
    setCurrentStep('Création montage PRO...');

    try {
      const token = localStorage.getItem('audio_token');
      await axios.post(`${API_URL}/api/video/create-montage`,
        { generation_id: generationId },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      startPollingGeneration(generationId);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Erreur montage');
    }
  };

  const resetAll = () => {
    setStep(1);
    setLoading(false);
    setError(null);
    setProgress(0);
    setStatus('idle');
    setTitle('');
    setPrompt('');
    setSlogan('');
    setVideoFormat('landscape');
    setAudioFile(null);
    setImages([]);
    setGenerationId(null);
    setGeneratedImage(null);
    setGeneratedImagesOptions([]);
    setSelectedImageIndex(0);
    setFinalVideo(null);
    setAdditionalImages([]);
    setAdditionalVideos([]);
    setNamesText('');
    if (pollInterval.current) clearInterval(pollInterval.current);
  };

  const FormatSelector = () => {
    const formatIcons = {
      landscape: Monitor, portrait: Smartphone, square: Square,
      story: Smartphone, reel: Smartphone, facebook: Facebook
    };

    return (
      <div>
        <label className="block text-white font-semibold mb-3">📐 Format Vidéo *</label>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(availableFormats).map(([key, format]) => {
            const Icon = formatIcons[key] || Monitor;
            return (
              <button key={key} onClick={() => setVideoFormat(key)}
                className={`p-4 rounded-xl transition-all ${
                  videoFormat === key
                    ? 'bg-gradient-to-r from-pink-600 to-red-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                <Icon className="w-8 h-8 mx-auto mb-2" />
                <div className="text-sm font-semibold">{format.name}</div>
                <div className="text-xs opacity-75">{format.width}x{format.height}</div>
                <div className="text-xs opacity-60">{format.ratio}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-900 via-red-900 to-rose-950 flex items-center justify-center p-4">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-pink-500 to-red-500 rounded-full mb-6">
              <Heart className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-red-400 to-rose-400">
                Montage Vidéo PRO
              </span>
            </h1>
            <p className="text-gray-300">✨ Qualité professionnelle ✨</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
            <div className="space-y-6">
              <input type="email" value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                placeholder="Email" required
                className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
              />
              <input type="password" value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Mot de passe" required
                className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
              />
              {loginError && (
                <div className="flex items-center space-x-2 text-red-400 bg-red-500/20 border border-red-500/50 rounded-xl p-3">
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm">{loginError}</span>
                </div>
              )}
              <button type="submit" className="w-full bg-gradient-to-r from-pink-600 via-red-600 to-rose-600 text-white py-4 rounded-xl font-bold hover:scale-105 transition-all">
                Se connecter
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-900 via-red-900 to-rose-950 pt-20 pb-20">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-between mb-6">
            <a href="/" className="text-white/70 hover:text-white transition-colors text-sm inline-flex items-center space-x-2">
              <ArrowLeft className="w-4 h-4" />
              <span>Accueil</span>
            </a>
            
            <div className="flex items-center space-x-4">
              {currentUser && <span className="text-white/70 text-sm">💝 {currentUser.name || currentUser.email}</span>}
              <button onClick={handleLogout} className="text-white/70 hover:text-white transition-colors text-sm flex items-center space-x-1">
                <Lock className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>

          <Heart className="w-16 h-16 text-pink-400 mx-auto mb-6 animate-pulse" />
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-red-400 to-rose-400">
              Montage Vidéo PRO ✨
            </span>
          </h1>
          <p className="text-xl text-gray-300">Textes dorés • Formats adaptés • Zoom varié • Frame fin 5s</p>
        </div>

        {loading && (
          <div className="max-w-4xl mx-auto mb-12 bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
            <div className="flex justify-between mb-4">
              {[
                { num: 1, name: 'Upload', icon: Upload },
                { num: 2, name: 'Image AI', icon: ImageIcon },
                { num: 3, name: 'Médias', icon: Plus },
                { num: 4, name: 'Prêt', icon: Settings },
                { num: 5, name: 'Montage', icon: Film },
                { num: 6, name: 'Terminé', icon: CheckCircle }
              ].map((s) => (
                <div key={s.num} className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    step >= s.num ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-white/10 text-gray-400'
                  }`}>
                    {step === s.num && loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <s.icon className="w-6 h-6" />}
                  </div>
                  <span className="text-xs text-gray-400">{s.name}</span>
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-700/50 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink-500 via-red-500 to-rose-500 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-center text-white mt-4">{currentStep}</p>
            <p className="text-center text-gray-400 text-sm">{Math.round(progress)}%</p>
          </div>
        )}

        {/* Étape 1: Upload avec formats */}
        {step === 1 && !loading && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 space-y-6">
              <FormatSelector />
              
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Ma Vidéo St-Valentin"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
              />
              
              <div>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Décrivez l'ambiance..." rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 resize-none"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {valentinePrompts.map((p, i) => (
                    <button key={i} onClick={() => setPrompt(p)}
                      className="text-xs px-3 py-1 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-full">
                      {p.substring(0, 35)}...
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <input type="text" value={slogan} onChange={(e) => setSlogan(e.target.value)}
                  placeholder="Forever Yours ❤️"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {valentineSlogans.map((s, i) => (
                    <button key={i} onClick={() => setSlogan(s)}
                      className="text-xs px-3 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 rounded-full">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 hover:border-pink-500/50 transition-colors">
                <input type="file" accept=".mp3,.wav,.m4a,.aac" onChange={handleAudioChange} className="hidden" id="audio-upload" />
                <label htmlFor="audio-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-white">{audioFile ? audioFile.name : 'Sélectionner audio'}</span>
                </label>
              </div>
              
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 hover:border-pink-500/50 transition-colors">
                <input type="file" accept=".png,.jpg,.jpeg,.webp" multiple onChange={handleImagesChange} className="hidden" id="images-upload" />
                <label htmlFor="images-upload" className="cursor-pointer flex flex-col items-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-white">{images.length > 0 ? `${images.length} image(s)` : 'Sélectionner images'}</span>
                </label>
              </div>
              
              {images.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {images.map((img, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden border border-white/20">
                      <img src={URL.createObjectURL(img)} alt={`Preview ${i+1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="text-center">
              <button onClick={handleUpload} disabled={loading || !audioFile || images.length === 0 || !prompt.trim()}
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-pink-600 via-red-600 to-rose-600 text-white px-12 py-5 rounded-2xl font-bold text-xl hover:scale-105 transition-all disabled:opacity-50">
                <Sparkles className="w-6 h-6" />
                <span>Créer Vidéo PRO ✨</span>
              </button>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6">
                <div className="flex items-center space-x-3">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <p className="text-red-300">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ✅ NOUVEAU : Étape 3 avec choix d'images Grok */}
        {step === 3 && !loading && (
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-3xl p-8">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Images générées ! 🎨</h2>
              <p className="text-gray-300">Choisissez votre image préférée puis ajoutez d'autres médias</p>
            </div>

            {/* ✅ SÉLECTION D'IMAGES */}
            {generatedImagesOptions.length > 1 && (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  ✨ Choisissez votre image préférée ✨
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {generatedImagesOptions.map((imgUrl, index) => (
                    <div key={index} className="relative">
                      <button
                        onClick={() => handleSelectGrokImage(index)}
                        className={`relative block w-full rounded-2xl overflow-hidden transition-all ${
                          selectedImageIndex === index
                            ? 'ring-4 ring-pink-500 scale-105'
                            : 'ring-2 ring-white/20 hover:ring-pink-400/50 hover:scale-102'
                        }`}
                      >
                        <img src={imgUrl} alt={`Option ${index + 1}`} className="w-full" />
                        {selectedImageIndex === index && (
                          <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-red-500 text-white p-3 rounded-full shadow-lg">
                            <Check className="w-6 h-6" />
                          </div>
                        )}
                      </button>
                      <p className="text-center text-white mt-3 font-semibold">
                        Option {index + 1}
                        {selectedImageIndex === index && <span className="text-pink-400 ml-2">✓ Sélectionnée</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* IMAGE SÉLECTIONNÉE */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Image sélectionnée :</h3>
              <img src={generatedImage} alt="Selected" className="w-full rounded-2xl" />
            </div>

            {/* AJOUTER MÉDIAS */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 space-y-6">
              <h3 className="text-2xl font-bold text-white mb-4">Ajouter d'autres médias</h3>
              
              <input type="text" value={namesText} onChange={(e) => setNamesText(e.target.value)}
                placeholder="Julie & Thomas"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-pink-500"
              />
              
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 hover:border-pink-500/50 transition-colors">
                <input type="file" accept=".png,.jpg,.jpeg,.webp" multiple
                  onChange={(e) => setAdditionalImages([...additionalImages, ...Array.from(e.target.files)])}
                  className="hidden" id="add-images" />
                <label htmlFor="add-images" className="cursor-pointer flex flex-col items-center">
                  <Plus className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-white">Ajouter images ({additionalImages.length})</span>
                </label>
              </div>
              
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 hover:border-pink-500/50 transition-colors">
                <input type="file" accept=".mp4,.mov,.avi,.webm" multiple
                  onChange={(e) => setAdditionalVideos([...additionalVideos, ...Array.from(e.target.files)])}
                  className="hidden" id="add-videos" />
                <label htmlFor="add-videos" className="cursor-pointer flex flex-col items-center">
                  <Film className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-white">Ajouter vidéos ({additionalVideos.length})</span>
                </label>
              </div>
              
              <button onClick={uploadAdditionalMedia}
                className="w-full bg-gradient-to-r from-pink-600 to-red-600 text-white py-4 rounded-xl font-bold hover:scale-105 transition-all">
                {additionalImages.length + additionalVideos.length > 0 ? '✨ Upload ' : 'Continuer sans médias'}
              </button>
            </div>
          </div>
        )}

        {/* Étape 4: Prêt */}
        {step === 4 && !loading && (
          <div className="max-w-4xl mx-auto bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Images prêtes ! 🎉</h2>
            <button onClick={handleCreateMontage}
              className="w-full bg-gradient-to-r from-pink-600 via-red-600 to-rose-600 text-white py-5 rounded-2xl font-bold text-xl hover:scale-105 transition-all">
              <Sparkles className="w-6 h-6 inline mr-2" />
              Créer Montage Final ✨
            </button>
          </div>
        )}

        {/* Étape 6: Résultat */}
        {step === 6 && finalVideo && (
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-3xl p-8">
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-white mb-2">Vidéo PRO créée ! 🎉</h2>
              <p className="text-gray-300">Zoom varié • Frame fin 5s • Sans image originale</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <video src={finalVideo} controls className="w-full rounded-2xl mb-6" />
              
              <div className="grid grid-cols-3 gap-4 text-center mb-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-gray-400 text-sm">Format</p>
                  <p className="text-white text-xl font-bold">{availableFormats[videoFormat]?.name}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-gray-400 text-sm">Durée</p>
                  <p className="text-white text-xl font-bold">{audioDuration.toFixed(1)}s + 5s</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-gray-400 text-sm">Résolution</p>
                  <p className="text-white text-xl font-bold">{availableFormats[videoFormat]?.width}x{availableFormats[videoFormat]?.height}</p>
                </div>
              </div>

              <a href={finalVideo} download="video_st_valentin_pro.mp4"
                className="w-full bg-gradient-to-r from-pink-600 to-red-600 text-white py-4 rounded-xl font-bold flex items-center justify-center space-x-2 hover:scale-105 transition-all">
                <Download className="w-5 h-5" />
                <span>Télécharger Vidéo PRO</span>
              </a>
            </div>

            <div className="text-center">
              <button onClick={resetAll}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all">
                Nouvelle Vidéo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoMontageCreatorPro;